/**
 * ChatMarkdown — the one seam every piece of V2 chat markdown renders
 * through (bead terra-design-v2-chat-yub.4).
 *
 * ORIGINALLY built on the pinned Glaze `Markdown` subpath export (Streamdown
 * underneath). Reverted off that stack as a Codex-adversarial-review
 * block-ship fix: Glaze's `Markdown` (`markdown-C6ztFXW3.js`'s `a` renderer)
 * hands every link to its `TextLink`/`Link` as `href: { pathname: href }` —
 * an OBJECT, not a string. `Link`'s own `checkIfValidExternalAnchorTag` does
 * `typeof href !== "string"` and so treats EVERY markdown link as internal,
 * routing it through `react-router-dom`'s `Link` with
 * `to: { pathname: '<the full absolute URL>' }`. That's harmless for a
 * query-string-free URL but THROWS for any URL containing `?`
 * (react-router's `resolveTo` rejects a `?` in a manually-specified
 * `to.pathname` field) — and Gong/Salesforce links, exactly what real
 * Keystone answers cite, almost always have one. Glaze's `Markdown` accepts
 * no element-`components` override to intercept this at the call site, so
 * there is no fix available while routed through it.
 *
 * FIX: render through the same stack V1's `account-view/keystone/
 * MarkdownText.tsx` already uses in production — `react-markdown` +
 * `remarkGfm`, with the shared `components/shared/markdownComponents.tsx`
 * map (explicitly documented there as working with plain `react-markdown`,
 * not just assistant-ui's `MarkdownTextPrimitive`). That map's `a` renderer
 * emits a plain `<a href>` tag, so a `?` in the URL is a non-issue.
 * `MarkdownTextPrimitive` itself isn't reusable here: it reads the message
 * text from assistant-ui's internal `useMessagePartText()` context, so it
 * only works mounted AS a `MessagePrimitive.Parts`'s `Text` slot — this
 * component is also called with a plain string that is NOT a message part
 * (`ChatRunSteps`'s reasoning text), so it needs a `children: string` API,
 * which only the bare `react-markdown` half of that stack provides.
 *
 * Known, accepted regressions from dropping Streamdown (both already true
 * of V1's chat in production, so this is a reversion to an
 * already-shipped-and-fine baseline, not a new one):
 *  - No per-word streaming fade-in animation.
 *  - No "closing" of unterminated markdown syntax mid-stream (e.g. a
 *    half-arrived `**bold` shows literal asterisks for a moment instead of
 *    an in-progress `<strong>`) — `react-markdown` has no equivalent to
 *    Streamdown's `remend`/incomplete-markdown parser.
 *  - Fenced code blocks render with the shared map's unconditional
 *    inline-code style (no bordered block / copy header) because the
 *    inline-vs-block distinction assistant-ui's `MarkdownText.tsx` gets from
 *    `useIsMarkdownCodeBlock` depends on an internal, non-exported
 *    `@assistant-ui/react-markdown` context that only exists inside
 *    `MarkdownTextPrimitive` itself — see `markdownComponents.tsx`'s own
 *    comment on this fallback. Keystone's account/call-summary answers have
 *    not been observed to contain fenced code, so this is a low-likelihood,
 *    purely cosmetic gap, not a crash — recorded here rather than papered
 *    over, per this bead's own instructions.
 *
 * `streaming` is still caller-computed (`msg.type === 'assistant' &&
 * !msg.stats && stream.isRunning`, covering the three completion
 * transitions: a final frame with `stats`, an `error` frame, and a
 * stopped/frozen session) but no longer changes rendering behavior here —
 * kept on the prop signature for call-site stability; see the regressions
 * list above for what `streaming` used to control.
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { unstable_memoizeMarkdownComponents as memoizeMarkdownComponents } from '@assistant-ui/react-markdown';
import { markdownComponents } from '../../../shared/markdownComponents';

export type ChatMarkdownVariant = 'body' | 'reasoning';

const VARIANT_WRAPPER_CLASS: Record<ChatMarkdownVariant, string> = {
  body: 'text-sm text-content-primary',
  reasoning: 'text-xs text-content-secondary italic',
};

const components = memoizeMarkdownComponents(markdownComponents);

export interface ChatMarkdownProps {
  children: string;
  /** Caller-computed — see the file header. Never derive this here. Kept
   *  for call-site stability; no longer changes rendering (see header). */
  streaming: boolean;
  /** @default 'body' */
  variant?: ChatMarkdownVariant;
  className?: string;
}

export function ChatMarkdown({ children, variant = 'body', className }: ChatMarkdownProps) {
  const wrapperClass = [VARIANT_WRAPPER_CLASS[variant], className].filter(Boolean).join(' ');

  return (
    <div className={wrapperClass}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
